/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import {useIntl} from 'react-intl'
import useMultiSite from '../../hooks/use-multi-site'
import {REGEX, LEGAL_AGE_EU, LEGAL_AGE_US} from '../../constants'
import useCountryList from '../../hooks/use-country-list'
import {validatePassword, usePasswordValidationMessages} from '../../utils/password-utils'
import {validateBirthday, useBirthdayValidationMessages} from '../../utils/birthday-utils'
import {useEffect, useState} from 'react'
import {useXSSProtection} from './../../hooks/use-xss-protection'
import useShipToCountry from '../../hooks/use-ship-to-country'
import {useToast} from '../../hooks/use-toast'

/**
 * Custom hook that generates and returns a set of registration form fields with validation rules
 * and error handling. This hook is designed to be used with React Hook Form and provides a
 * structured way to manage form inputs for user registration.
 *
 * @param {Object} params - The parameters for the hook.
 * @param {Object} params.form - The form object containing methods and state from React Hook Form.
 * @param {string} [params.prefix=''] - An optional prefix to prepend to the names of form fields, useful for namespacing.
 *
 * @returns {Object} An object containing configuration for each registration form field.
 */
export default function useRegistrationFields({
    form: {
        control,
        formState: {errors},
        watch,
        setValue,
        clearErrors
    },
    prefix = ''
}) {
    const toast = useToast()
    const {formatMessage} = useIntl()
    const passwordValidationMessages = usePasswordValidationMessages()
    const birthdayValidationMessages = useBirthdayValidationMessages()
    const {locale} = useMultiSite()
    const countries = useCountryList()
    const countryCode = watch(`${prefix}countryCode`)
    const phone = watch(`${prefix}phone`)
    const {shipToCountry} = useShipToCountry()

    // Set the default phone country code based on the selected shipping country.
    const defaultCountryCode = locale?.id.split('-')[1]
    const finalCountryCode = shipToCountry?.id ? shipToCountry.id : defaultCountryCode

    const [defaultPhoneCountry, setDefaultPhoneCountry] = useState(finalCountryCode)

    // Sync phone country code on empty phone field
    useEffect(() => {
        if (phone === '') {
            setDefaultPhoneCountry(countryCode)
        }
    }, [countryCode])

    const isUS = countryCode === 'US'

    useEffect(() => {
        if (!isUS) {
            clearErrors(`${prefix}birthday`)
        }
    }, [countryCode, clearErrors, prefix, isUS])

    const {validateField} = useXSSProtection()

    const fields = {
        firstName: {
            name: `${prefix}firstName`,
            label: formatMessage({
                defaultMessage: 'First Name',
                id: 'use_registration_fields.label.first_name'
            }),
            type: 'text',
            autoComplete: 'given-name',
            defaultValue: '',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter your first name.',
                    id: 'use_registration_fields.error.required_first_name'
                }),
                validate: (value) => validateField(toast, value, 'registration-form', 'firstname')
            },
            error: errors[`${prefix}firstName`],
            control
        },
        lastName: {
            name: `${prefix}lastName`,
            label: formatMessage({
                defaultMessage: 'Last Name',
                id: 'use_registration_fields.label.last_name'
            }),
            type: 'text',
            defaultValue: '',
            autoComplete: 'family-name',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter your last name.',
                    id: 'use_registration_fields.error.required_last_name'
                }),
                validate: (value) => validateField(toast, value, 'registration-form', 'lastname')
            },
            error: errors[`${prefix}lastName`],
            control
        },
        email: {
            name: `${prefix}email`,
            label: formatMessage({
                defaultMessage: 'Email Address',
                id: 'use_registration_fields.label.email'
            }),
            placeholder: 'you@email.com',
            type: 'email',
            autoComplete: 'email',
            defaultValue: '',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter your email address.',
                    id: 'use_registration_fields.error.required_email'
                }),
                pattern: {
                    value: REGEX.EMAIL,
                    message: formatMessage({
                        defaultMessage: 'Please enter a valid email address.',
                        id: 'use_registration_fields.error.invalid_email'
                    })
                },
                validate: (value) => validateField(toast, value, 'registration-form', 'email')
            },
            error: errors[`${prefix}email`],
            control
        },
        birthday: {
            name: `${prefix}birthday`,
            label: formatMessage({
                defaultMessage: 'Date of Birth',
                id: 'use_registration_fields.label.birthday'
            }),
            type: 'date',
            defaultValue: '',
            autoComplete: 'bday',
            rules: {
                required: isUS ? birthdayValidationMessages.required : false,
                validate: {
                    isPastDate: (value) =>
                        validateBirthday(value).isPastDate || birthdayValidationMessages.invalid,
                    hasLegalAge: (value) => {
                        const ageLimit = isUS ? LEGAL_AGE_US : LEGAL_AGE_EU
                        return (
                            validateBirthday(value).hasLegalAge(ageLimit) ||
                            birthdayValidationMessages.hasLegalAge(ageLimit)
                        )
                    }
                }
            },
            error: errors[`${prefix}birthday`],
            control
        },
        countryCode: {
            name: `${prefix}countryCode`,
            label: formatMessage({
                defaultMessage: 'Country',
                id: 'use_registration_fields.label.country'
            }),
            defaultValue: finalCountryCode,
            type: 'select',
            autoComplete: 'country',
            options: countries,
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter your country.',
                    id: 'use_registration_fields.error.required_country'
                })
            },
            error: errors[`${prefix}countryCode`],
            control
        },
        phone: {
            name: `${prefix}phone`,
            label: formatMessage({
                defaultMessage: 'Phone Number',
                id: 'use_registration_fields.label.phone'
            }),
            defaultValue: '',
            autoComplete: 'tel',
            defaultCountry: defaultPhoneCountry,
            error: errors[`${prefix}phone`],
            control,
            setValue, // pass setValue to update dial select on defaultCountry update
            clearErrors
        },
        password: {
            name: `${prefix}password`,
            label: formatMessage({
                defaultMessage: 'Password',
                id: 'use_registration_fields.label.password'
            }),
            type: 'password',
            autoComplete: 'new-password',
            defaultValue: '',
            rules: {
                required: passwordValidationMessages.required.create_password,
                validate: {
                    hasMinChars: (val) =>
                        validatePassword(val).hasMinChars || passwordValidationMessages.minChars,
                    hasUppercase: (val) =>
                        validatePassword(val).hasUppercase || passwordValidationMessages.uppercase,
                    hasLowercase: (val) =>
                        validatePassword(val).hasLowercase || passwordValidationMessages.lowercase,
                    hasNumber: (val) =>
                        validatePassword(val).hasNumber || passwordValidationMessages.number,
                    hasSpecialChar: (val) =>
                        validatePassword(val).hasSpecialChar ||
                        passwordValidationMessages.specialChar
                }
            },
            error: errors[`${prefix}password`],
            control
        },
        shareConsent: {
            name: `${prefix}shareConsent`,
            label: formatMessage({
                defaultMessage:
                    'I agree to my data and purchase preferences being shared only within the Vilebrequin family for analysis and for a unique and personalized experience',
                id: 'use_registration_fields.label.share_consent'
            }),
            type: 'checkbox',
            defaultValue: false,
            rules: {
                required: formatMessage({
                    defaultMessage: 'This field is required.',
                    id: 'use_registration_fields.error.required_share_consent'
                })
            },
            error: errors[`${prefix}shareConsent`],
            control
        },
        marketingConsent: {
            name: `${prefix}marketingConsent`,
            label: formatMessage({
                defaultMessage:
                    'I agree to receive the latest news and promotional offers via the means of communication that I have sent you.',
                id: 'use_registration_fields.label.marketing_consent'
            }),
            type: 'checkbox',
            defaultValue: false,
            error: errors[`${prefix}marketingConsent`],
            control
        }
    }

    return fields
}
